import React from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

interface KPICardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  colorIndex?: number;
  trend?: {
    value: number; // percentage
    direction: 'up' | 'down' | 'neutral';
    isPositive?: boolean; // green or red (e.g. churn going up is negative)
  };
}

const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  subtitle,
  colorIndex = 0,
  trend,
}) => {
  const color = CHART_PALETTE[colorIndex % CHART_PALETTE.length];

  return (
    <div
      className="stat-card relative overflow-hidden"
      style={{
        background: SURFACE.card,
      }}
    >
      {/* Accent bar at top */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, ${color.gradient[0]}, ${color.gradient[1]})` }}
      />

      {/* Label */}
      <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: SURFACE.textMuted }}>
        {label}
      </div>

      {/* Value */}
      <div className="flex items-end gap-2">
        <div
          className="text-3xl font-bold leading-none tabular-nums"
          style={{ color: SURFACE.text }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>

        {/* Trend indicator */}
        {trend && (
          <div
            className="flex items-center gap-0.5 text-xs font-semibold mb-0.5 px-1.5 py-0.5 rounded-full"
            style={{
              color: trend.isPositive !== false
                ? (trend.direction === 'up' ? '#059669' : '#DC2626')
                : (trend.direction === 'up' ? '#DC2626' : '#059669'),
              background: trend.isPositive !== false
                ? (trend.direction === 'up' ? '#D1FAE5' : '#FEE2E2')
                : (trend.direction === 'up' ? '#FEE2E2' : '#D1FAE5'),
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              {trend.direction === 'up' ? (
                <path d="M5 2L8.5 6.5H1.5L5 2Z" />
              ) : trend.direction === 'down' ? (
                <path d="M5 8L1.5 3.5H8.5L5 8Z" />
              ) : (
                <path d="M2 5.5H8V4.5H2V5.5Z" />
              )}
            </svg>
            {Math.abs(trend.value).toFixed(1)}%
          </div>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="text-xs mt-2" style={{ color: SURFACE.textMuted }}>
          {subtitle}
        </div>
      )}

      {/* Subtle corner decoration */}
      <div
        className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full"
        style={{
          background: `radial-gradient(circle, ${color.base}08, transparent)`,
        }}
      />
    </div>
  );
};

export default KPICard;
